import { Box } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ServiceType } from '@common/enums';
import type { IServiceStartingPoint } from '@common/interfaces';
import type {
    orthoServices,
    ProcessStartWizardItem,
    ServiceForSelectedServiceType,
    ServiceTypeWithServices,
} from '@common/interfaces/processStartWizard';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import { EpisodeCategory, ServiceCode } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { AbortableFetch } from '@frontend/store/fetch';
import { getStartProcessUrl } from '@frontend/utils/getStartProcessUrl';
import { generateUrlForRoute } from '@frontend/utils/hooks/useNavigateTo';
import { scrollToBottomWithinContainer } from '@frontend/utils/scrollUtils';
import { GgzoEmblActionBar } from '../customControls/GGZOembl/v1/GgzoEmblActionBar';
import { addTrackEvent } from '../customControls/utils/formAnalytics';
import { EpisodeCategoryButtons } from './components/EpisodeCategoryButtons';
import { OrthopticsServicesButtonRow } from './components/OrthopticsServicesButtonRow';
import { PatientSearchRow } from './components/PatientSearchRow';
import { ProcessStartWizardCard } from './components/ProcessStartWizardCard';
import { ServiceCardsAndStartingPointsCards } from './components/serviceCardsAndStartingPointCards';
import { ServiceTypeButtons } from './components/ServiceTypeButtons';

interface IProcessStartWizardProps {
    episodeCategory?: EpisodeCategory | null;
    onAddProcessToEpisode?: (args: ServiceUrlObject) => Promise<void> | void;
    onShowOverlay: (showOverlay: boolean) => void;
    patientId?: string;
    serviceType?: ServiceType | null;
}

export function ProcessStartWizard(props: IProcessStartWizardProps) {
    const { onShowOverlay, patientId, onAddProcessToEpisode } = props;
    const abortableFetch = new AbortableFetch();
    const [processStartWizardObject, setProcessStartWizardObject] = useState<ProcessStartWizardItem[]>([]);
    const [selectedEpisodeCategory, setSelectedEpisodeCategory] = useState<ProcessStartWizardItem>();

    const [serviceTypes, setServiceTypes] = useState<ServiceTypeWithServices[]>([]);
    const [selectedServiceType, setSelectedServiceType] = useState<ServiceTypeWithServices>();

    const [servicesForSelectedType, setServicesForSelectedType] = useState<ServiceForSelectedServiceType[]>([]);
    const [selectedService, setSelectedService] = useState<ServiceForSelectedServiceType>();

    const [hideEpisodeCategoryButtons] = useState(!!props.episodeCategory);
    const [newProcessCustomIdentifier, setNewProcessCustomIdentifier] = useState<string>();

    const [selectedOrthopticsService, setSelectedOrhopticsService] = useState<orthoServices>();

    const [startingPointsForSelectedService, setStartingPointsForSelectedService] = useState<IServiceStartingPoint[]>(
        []
    );
    const [selectedStartingPoint, setSelectedStartingPoint] = useState<IServiceStartingPoint>();

    useEffect(() => {
        loadProcessStartWizard();
    }, []);

    useEffect(() => {
        if (props.episodeCategory) {
            setSelectedEpisodeCategory(
                processStartWizardObject.find((item) => item.episodeCategory === props.episodeCategory)
            );
        }
    }, [processStartWizardObject]);

    useEffect(() => {
        if (selectedEpisodeCategory) {
            setServiceTypes(selectedEpisodeCategory.serviceTypes);
            if (!hideEpisodeCategoryButtons) {
                onShowOverlay(true);
            }
        } else {
            setServiceTypes([]);
            onShowOverlay(false);
        }
    }, [selectedEpisodeCategory]);

    useEffect(() => {
        if (serviceTypes.length === 1) {
            setSelectedServiceType(serviceTypes[0]);
        } else {
            if (!props.serviceType) {
                setSelectedServiceType(undefined);
            }
        }
    }, [serviceTypes]);

    useEffect(() => {
        if (props.serviceType && selectedEpisodeCategory) {
            setSelectedServiceType(
                selectedEpisodeCategory.serviceTypes.find((item) => item.serviceType === props.serviceType)
            );
        }
    }, [props.serviceType, selectedEpisodeCategory]);

    useEffect(() => {
        if (selectedServiceType) {
            setServicesForSelectedType(selectedServiceType.services);
            if (
                selectedServiceType.services.length === 1 &&
                selectedServiceType.services[0].startingPoints &&
                selectedServiceType.services[0].startingPoints?.length > 0
            ) {
                setStartingPointsForSelectedService(selectedServiceType.services[0].startingPoints);
            }
        } else {
            setServicesForSelectedType([]);
        }
    }, [selectedServiceType]);

    useEffect(() => {
        if (selectedService?.startingPoints && selectedService.startingPoints.length > 0) {
            setStartingPointsForSelectedService(selectedService.startingPoints);
        } else {
            setStartingPointsForSelectedService([]);
        }
    }, [selectedService]);

    useEffect(() => {
        if (
            servicesForSelectedType.length === 1 &&
            ((servicesForSelectedType[0].startingPoints && servicesForSelectedType[0].startingPoints.length > 0) ||
                !patientId)
        ) {
            setSelectedService(servicesForSelectedType[0]);
        } else {
            setSelectedService(undefined);
        }
    }, [servicesForSelectedType]);

    useEffect(() => {
        resetSelectedOptionsForPreviouslySelectedService();
    }, [selectedService]);

    useEffect(() => {
        if (canStartProcess()) {
            startProcess();
        }
    }, [newProcessCustomIdentifier, patientId, selectedOrthopticsService, selectedService, selectedStartingPoint]);

    return (
        <>
            <ProcessStartWizardCard>
                {processStartWizardObject.length > 0 && !hideEpisodeCategoryButtons && (
                    <EpisodeCategoryButtons
                        episodeCategories={processStartWizardObject}
                        onClick={onClickEpisodeCategory}
                        onClose={onClose}
                        selectedEpisodeCategory={selectedEpisodeCategory?.episodeCategory}
                        showCloseButton={Boolean(selectedEpisodeCategory)}
                    />
                )}

                {selectedEpisodeCategory && serviceTypes.length > 1 && (
                    <ServiceTypeButtons
                        onClick={onClickServiceType}
                        selectedServiceType={selectedServiceType?.serviceType}
                        serviceTypes={serviceTypes}
                    />
                )}
            </ProcessStartWizardCard>

            {selectedServiceType && servicesForSelectedType.length > 0 && (
                <ServiceCardsAndStartingPointsCards
                    onClickService={onClickService}
                    onClickStartingPoint={onClickStartingPoint}
                    onClickSubService={onClickOrthopticsService}
                    patientId={patientId}
                    selectedService={selectedService}
                    selectedStartingPoint={selectedStartingPoint}
                    services={servicesForSelectedType}
                />
            )}

            {selectedService && selectedService.code === ServiceCode.GGZOembl && (
                <Box mt={4}>
                    <ProcessStartWizardCard>
                        <GgzoEmblActionBar
                            hideLabel
                            service={selectedService}
                            setNewProcessCustomIdentifier={async (option?: string) =>
                                setNewProcessCustomIdentifier(option)
                            }
                        />
                    </ProcessStartWizardCard>
                </Box>
            )}

            {!patientId &&
                selectedService &&
                (selectedService.code === ServiceCode.OrthO || selectedService.code === ServiceCode.OrthOFol) &&
                selectedService.services &&
                selectedService.services.length > 0 && (
                    <Box mt={4}>
                        <ProcessStartWizardCard>
                            <OrthopticsServicesButtonRow
                                onClick={onClickOrthopticsService}
                                selectedService={selectedOrthopticsService}
                                services={selectedService.services}
                            />
                        </ProcessStartWizardCard>
                    </Box>
                )}

            {showPatientSearchRow() && (
                <Box mt={4}>
                    <PatientSearchRow
                        newProcessCustomIdentifier={newProcessCustomIdentifier}
                        selectedService={selectedOrthopticsService || selectedService}
                        selectedStartingPoint={selectedStartingPoint}
                    />
                </Box>
            )}
        </>
    );

    async function loadProcessStartWizard() {
        const urlBuilder = new UrlBuilder('/api/episodeCategories/processStartWizard');
        if (patientId) {
            urlBuilder.add({
                patientId: patientId || null,
            });
        }
        const url = urlBuilder.build();
        const result = await abortableFetch.getJson(url);

        if (result.json) {
            setProcessStartWizardObject(result.json.items);
        }

        return [];
    }

    function onClickEpisodeCategory(name: EpisodeCategory) {
        if (name === EpisodeCategory.mentalHealth) {
            addTrackEvent('ProcessStartWizard', 'Mentalhealth clicked', 'User selects mentalhealth category');
        }

        setSelectedEpisodeCategory(processStartWizardObject.find((item) => item.episodeCategory === name));
    }

    function onClickServiceType(name: ServiceType) {
        setSelectedServiceType(serviceTypes.find((item) => item.serviceType === name));
    }

    function onClickService(service: ServiceForSelectedServiceType) {
        setSelectedService(service);
    }

    function onClickStartingPoint(startingPoint: IServiceStartingPoint, service: ServiceForSelectedServiceType) {
        setSelectedStartingPoint(startingPoint);
        setSelectedService(service);
    }

    function onClickOrthopticsService(
        code: ServiceCode.OrthO | ServiceCode.OrthOFol,
        service?: ServiceForSelectedServiceType
    ) {
        if (service) {
            setSelectedService(service);
            setSelectedOrhopticsService(service.services?.find((item) => item.code === code));
        } else {
            setSelectedOrhopticsService(selectedService?.services?.find((item) => item.code === code));
        }
    }

    async function startProcess() {
        const serviceToStart = selectedOrthopticsService || selectedService;

        if (selectedStartingPoint && patientId) {
            const url = generateUrlForRoute(
                'patientCarePath',
                {
                    activeTab: 'carePath',
                    episodeCategory: selectedStartingPoint.service.episodeCategory.name,
                    patientId,
                },
                {
                    startingPointId: selectedStartingPoint.id,
                }
            );

            window.location.href = url;
        } else if (serviceToStart && patientId) {
            if (onAddProcessToEpisode) {
                await onAddProcessToEpisode({
                    isExternalHolter:
                        serviceToStart.isExternalHolter !== undefined
                            ? serviceToStart.isExternalHolter.toString()
                            : undefined,
                    newProcessCustomIdentifier,
                    serviceId: serviceToStart.id,
                    withProcedure: Boolean(serviceToStart.isServiceProvider),
                });
            } else if (selectedEpisodeCategory?.episodeCategory) {
                const url = getStartProcessUrl({
                    episodeCategory: selectedEpisodeCategory?.episodeCategory,
                    newProcessCustomIdentifier,
                    patientId,
                    service: serviceToStart,
                });
                window.location.href = url;
            }

            scrollToBottomWithinContainer('scroll-container');
        }
    }

    function canStartProcess() {
        return (
            !!patientId &&
            !!selectedService &&
            (startingPointsForSelectedService.length === 0 || !!selectedStartingPoint) &&
            (selectedService.code !== ServiceCode.GGZOembl || !!newProcessCustomIdentifier) &&
            (!selectedService.services || !!selectedOrthopticsService)
        );
    }

    function showPatientSearchRow() {
        return (
            !patientId &&
            !!selectedService &&
            (startingPointsForSelectedService.length === 0 || !!selectedStartingPoint) &&
            (!selectedService.services || selectedOrthopticsService) &&
            (selectedService.code !== ServiceCode.GGZOembl || newProcessCustomIdentifier)
        );
    }

    function onClose() {
        setSelectedEpisodeCategory(undefined);
    }

    function resetSelectedOptionsForPreviouslySelectedService() {
        setSelectedOrhopticsService(undefined);
        setNewProcessCustomIdentifier(undefined);
    }
}
